/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  JSXElementConstructor,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor} from '../../../theme/theme';
import {SavedMapViewType} from '../../../types/types';
import SavedMapViewsChooserHeader from './SavedMapViewsChooserHeader';
import SavedMapViewsChooserInput from './SavedMapViewsChooserInput';
import SavedMapViewsChooserList from './SavedMapViewsChooserList';

type SavedMapViewsChooserProps = {
  view: __esri.MapView;
  setSavedMapView: Function;
  deleteSavedMapView: Function;
  editSavedMapView: Function;
  setDefaultMapView?: Function;
  onSaveMapView: Function;
  savedMapViews: SavedMapViewType[];
  isTracking?: boolean;
  isVisible?: boolean;
  showHeaderAlways?: boolean;
  isOpeningFromBottomSheet?: boolean;
  overideStyle?: GenericObject;
  toggleSavedMapViewTool?: Function;
  closeMapTools?: Function;
  MapControlsBreadCrumbs?: ReactElement<any, string | JSXElementConstructor<any>>
};

const toggleStyle = (prop: string, defaultvalue: string | number) =>
  (overideStyle) => overideStyle?.[prop] ?? defaultvalue
;

const useStyles = makeStyles(theme => ({
  mapView : {
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    boxShadow       : dlsAlt('components', 'boxShadow', 'shadow4'),
    position        : 'absolute',
    top             : toggleStyle('top', 109),
    width           : 300,
    maxHeight       : '100%',
    right           : 55
  },
  mapViewMobile : {
    padding  : '0px 8px 0px 8px',
    top      : toggleStyle('top', 0),
    position : toggleStyle('position', 'inherit')
  }
}));

const nameLimit = 25;

export const shortenName = (name: string) => {
  return `${name?.substring(0, nameLimit)}${name?.length > nameLimit ? '...' : ''}`;
};

/**
 * Reusable component for displaying Saved Map Views
 *
 * ```SavedMapViewsChooser
 *
 *   <SavedMapViewsChooser view={view}
 *     setSavedMapView={setSavedMapView}
 *     deleteSavedMapView={deleteSavedMapView}
 *     onSaveMapView={onSaveMapView}
 *     ....
 *   />
 *
 * ```
 */
const SavedMapViewsChooser = ({
  view,
  isTracking,
  isVisible,
  onSaveMapView,
  toggleSavedMapViewTool,
  isOpeningFromBottomSheet,
  closeMapTools,
  setSavedMapView,
  deleteSavedMapView,
  editSavedMapView,
  setDefaultMapView,
  MapControlsBreadCrumbs,
  overideStyle = {},
  savedMapViews = [],
  showHeaderAlways = false
}: SavedMapViewsChooserProps) => {
  const classes                                       = useStyles(overideStyle);
  const [isSaveMapForm, setIsSaveMapForm]             = useState(false);
  const [mapViewToEdit, setMapViewToEdit]             = useState<SavedMapViewType>(null);
  const viewClass = useMemo(() => {
    return isOpeningFromBottomSheet ? classes.mapViewMobile : classes.mapView;
  }, [isOpeningFromBottomSheet]);

  const resetToolState = useCallback(() => {
    setIsSaveMapForm(false);
    setMapViewToEdit(null);
  }, []);

  useEffect(() => {
    if (!savedMapViews?.length) {
      setIsSaveMapForm(true);
    }
  }, [savedMapViews]);

  const onSave = useCallback(({inputValue}) => {
    if (!mapViewToEdit) {
      const {extent, map: {basemap}} = view;
      onSaveMapView({
        id      : inputValue,
        basemap : basemap?.id,
        extent
      });
    } else {
      editSavedMapView(inputValue, mapViewToEdit);
    }
    resetToolState();
  }, [
    view,
    onSaveMapView,
    mapViewToEdit,
    savedMapViews,
    resetToolState
  ]);

  const onCancel = useCallback(() => {
    resetToolState();
    if (!savedMapViews?.length) {
      toggleSavedMapViewTool?.();
    }
  }, [
    resetToolState,
    toggleSavedMapViewTool,
    savedMapViews
  ]);

  const onDelete = useCallback((mapView, mapViews) => {
    deleteSavedMapView(mapView, mapViews);
  }, [deleteSavedMapView]);

  const onEdit = useCallback((mapView) => {
    setIsSaveMapForm(true);
    setMapViewToEdit(mapView);
  }, []);

  const onSetMapView = useCallback((mapView) => {
    setSavedMapView(mapView);
    resetToolState();
  }, [setSavedMapView]);

  const openSaveMapForm = useCallback(() => {
    setIsSaveMapForm(true);
  }, []);

  const onSetDefaultMapView = useCallback((value: boolean, mapView: SavedMapViewType) => {
    setDefaultMapView?.(value, mapView);
  }, []);

  return (
    <div style={{display: !isVisible ? 'none' : 'inherit'}}
      className={viewClass}>
      <SavedMapViewsChooserHeader isSaveMapForm={isSaveMapForm}
        resetToolState={resetToolState}
        savedMapViews={savedMapViews}
        openSaveMapForm={openSaveMapForm}
        mapViewToEdit={mapViewToEdit}
        showHeaderAlways={showHeaderAlways}
        MapControlsBreadCrumbs={MapControlsBreadCrumbs}/>
      {isSaveMapForm || !savedMapViews?.length ? (
        <SavedMapViewsChooserInput savedMapViews={savedMapViews}
          mapViewToEdit={mapViewToEdit}
          onCancel={onCancel}
          onSave={onSave}/>
      ) : (
        <SavedMapViewsChooserList savedMapViews={savedMapViews}
          isTracking={isTracking}
          onSetMapView={onSetMapView}
          onDelete={onDelete}
          onEdit={onEdit}
          onSetDefaultMapView={onSetDefaultMapView} />
      )}
    </div>
  );
};

export default SavedMapViewsChooser;
